import { API_BASE_URL } from "@/lib/env";
import { getAccessToken, signOut } from "@/services/authService";

export class AuthenticationError extends Error {
  constructor() {
    super("session_expired");
  }
}

/** A response outside 2xx from the generated client, with the parsed body. */
export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown
  ) {
    super(`HTTP ${status}`);
  }
}

/**
 * The backend's error code (`{ error: "attempts_exceeded" }`) from a failed
 * request, or undefined when the failure carried none.
 */
export const errorCodeOf = (error: unknown): string | undefined => {
  if (!(error instanceof HttpError)) return undefined;
  const body = error.body;
  if (typeof body !== "object" || body === null || !("error" in body)) {
    return undefined;
  }
  return typeof body.error === "string" ? body.error : undefined;
};

// Backend origin, derived from the API base URL. The generated client builds
// its paths from the OpenAPI spec (`/api/v1/...`) and VITE_API_BASE_URL already
// carries that prefix, so only the origin is put in front. Active Storage
// download paths (/rails/...) are served by the same origin.
export const API_ORIGIN = new URL(API_BASE_URL, window.location.origin).origin;

/**
 * Fetch with the signed-in learner's token.
 *
 * The learner's own token is the only credential this app has. There is no
 * static fallback: the backend takes its API token as a system caller with
 * access to every enrollment, so a signed-out browser must send nothing at
 * all and be turned away here.
 */
const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const headers = new Headers(options.headers);

  const token = await getAccessToken();
  if (!token) {
    throw new AuthenticationError();
  }

  headers.set("Authorization", `Bearer ${token}`);

  let res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    const freshToken = await getAccessToken();
    if (freshToken) {
      headers.set("Authorization", `Bearer ${freshToken}`);
      res = await fetch(url, { ...options, headers });
      if (res.status === 401) {
        // Backend rejected a freshly-obtained token — session is definitively gone.
        await signOut();
        throw new AuthenticationError();
      }
    } else {
      // Silent renewal failed (transient error, or getAccessToken already
      // escalated to an interactive redirect and navigation is pending).
      // Do not sign out — the session may still be valid.
      throw new AuthenticationError();
    }
  }

  return res;
};

/**
 * The HTTP client of the generated API (see orval.config.ts): resolves the
 * spec path against the backend origin, sends the learner's token and returns
 * the parsed JSON body, or throws an HttpError for a non-2xx response.
 */
export const apiFetch = async <T>(
  url: string,
  init?: RequestInit
): Promise<T> => {
  const res = await authenticatedFetch(`${API_ORIGIN}${url}`, init);
  const text = [204, 205, 304].includes(res.status) ? "" : await res.text();
  const data: unknown = text ? JSON.parse(text) : undefined;
  if (!res.ok) throw new HttpError(res.status, data);
  return data as T;
};
